/*
  Big B Comics — search query expansion (July 2026)

  Rewrites common comic/TCG abbreviations into the vocabulary the catalog
  actually uses (Diamond-style titles) before the query hits Shopify search.
  Applies to both predictive (dropdown) search and the full search page.

  "asm 300"  -> "amazing spider-man 300"
  "gotg 1"   -> "guardians of the galaxy 1"
  "mtg foundations" -> "magic the gathering foundations"

  Whole-word matches only, case-insensitive. To tune the map without
  editing this file, define window.BigBSearchExpansions BEFORE this script
  loads and it will be merged in (same-key entries override).

  Deliberately NOT included: terms Diamond keeps abbreviated in titles
  (TMNT, BPRD, GI JOE) and ambiguous short tokens (cap, op, ww).

  ALSO IN THIS FILE (Oct 2026), because this script already loads sitewide
  from theme.liquid:
   - block 2: the touch-screen card press lift
   - block 3: the "View all" end card on the homepage swipe rows
*/
(function () {
  var MAP = {
    asm: 'amazing spider-man',
    ff: 'fantastic four',
    xmen: 'x-men',
    'x men': 'x-men',
    gotg: 'guardians of the galaxy',
    jla: 'justice league',
    gl: 'green lantern',
    dd: 'daredevil',
    lotr: 'lord of the rings',
    mtg: 'magic the gathering',
    ygo: 'yu-gi-oh',
    yugioh: 'yu-gi-oh',
    dbz: 'dragon ball z',
    tpb: 'tp',
    'spiderman': 'spider-man',
    'spidey': 'spider-man'
  };

  // Merge user overrides if provided
  if (window.BigBSearchExpansions && typeof window.BigBSearchExpansions === 'object') {
    Object.keys(window.BigBSearchExpansions).forEach(function (k) {
      MAP[k.toLowerCase()] = window.BigBSearchExpansions[k];
    });
  }

  // Longest keys first so "x men" wins over "men"
  var KEYS = Object.keys(MAP).sort(function (a, b) {
    return b.length - a.length;
  });

  function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function expand(term) {
    if (!term) return term;
    var out = term;
    KEYS.forEach(function (key) {
      var re = new RegExp('(^|[\\s#])' + escapeRegExp(key) + '(?=$|[\\s#0-9])', 'gi');
      out = out.replace(re, function (m, lead) {
        return lead + MAP[key];
      });
    });
    return out;
  }

  // Expose for debugging / reuse
  window.bigbExpandSearch = expand;

  /* Predictive (dropdown) search: patch the custom element so the query is
     expanded before Dawn fetches suggestions. */
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('predictive-search').then(function () {
      var PS = customElements.get('predictive-search');
      if (!PS || !PS.prototype || typeof PS.prototype.getSearchResults !== 'function') return;
      if (PS.prototype.__bigbExpanded) return;
      PS.prototype.__bigbExpanded = true;
      var orig = PS.prototype.getSearchResults;
      PS.prototype.getSearchResults = function (searchTerm) {
        return orig.call(this, expand(searchTerm));
      };
    });
  }

  /* Full search page: expand q on submit of any search form. Capture phase
     so we rewrite before Dawn's own handlers run. */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/search') === -1) return;
      var q = form.querySelector('input[name="q"]');
      if (q && q.value) q.value = expand(q.value);
    },
    true
  );
})();

/*
  Big B Comics — product card press lift on touch screens (Oct 2026)

  Phones have no hover, so the desktop pop-out lift (component-card.css,
  min-width 990px) never fires there. This adds .bb-pressed to a product
  card while a finger is on it; bigb-mobile.css section 4 styles it to
  match the desktop hover. (iOS Safari won't reliably run a CSS :active
  state without a touch listener, which is why this is JS + a class.)

  - Touch only: mouse users keep the real :hover.
  - Swipes never lift: the card only pops once we know it's a tap, either
    after the finger has rested on it for 120ms without moving, or the
    moment the finger lifts without having moved. Any movement over 6px,
    or any scroll (the homepage rows scroll sideways), cancels it. Before
    this, a swipe started on a card popped it for a split second.
  - A tap keeps it up briefly so the lift is visible while the product page
    loads; it's cleared on pageshow so the Back button doesn't return you
    to a card stuck in the air.
  - Not on the quick-add button: that has its own press feedback.
*/
(function () {
  var CARD = '.product-card-wrapper';
  var SKIP = '.quick-add, button, select, input, quick-add-modal';
  var MOVE_TOLERANCE = 6;
  var HOLD_DELAY = 120;
  var pending = null; // card under the finger, not lifted yet
  var active = null; // card currently lifted
  var moved = false;
  var startX = 0;
  var startY = 0;
  var holdTimer = null;
  var releaseTimer = null;

  function lift(card) {
    active = card;
    card.classList.add('bb-pressed');
  }

  function clear() {
    clearTimeout(holdTimer);
    clearTimeout(releaseTimer);
    if (active) active.classList.remove('bb-pressed');
    active = null;
    pending = null;
  }

  document.addEventListener(
    'touchstart',
    function (e) {
      clear();
      if (e.touches.length !== 1) return;
      var t = e.target;
      if (!t.closest || t.closest(SKIP)) return;
      var card = t.closest(CARD);
      if (!card) return;
      pending = card;
      moved = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      holdTimer = setTimeout(function () {
        if (pending && !moved) lift(pending);
      }, HOLD_DELAY);
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    function (e) {
      if (!pending) return;
      var dx = e.touches[0].clientX - startX;
      var dy = e.touches[0].clientY - startY;
      if (dx * dx + dy * dy > MOVE_TOLERANCE * MOVE_TOLERANCE) {
        moved = true;
        clear();
      }
    },
    { passive: true }
  );

  // Sideways row scrolls and page scrolls both cancel (capture catches
  // scrolls inside the homepage sliders, which don't bubble)
  document.addEventListener(
    'scroll',
    function () {
      if (pending || active) {
        moved = true;
        clear();
      }
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'touchend',
    function () {
      if (!pending || moved) return;
      clearTimeout(holdTimer);
      if (!active) lift(pending); // quick tap: pop now
      pending = null;
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(clear, 450);
    },
    { passive: true }
  );

  document.addEventListener('touchcancel', clear, { passive: true });
  window.addEventListener('pageshow', clear);
})();

/*
  Big B Comics — "View all" end card on homepage swipe rows (Oct 2026)

  On phones each homepage product row swipes through 24 products. Once
  someone reaches the end, the section's "View all" button has scrolled off
  to the left, so this adds a final card in the row that links into the
  full collection. Styled in bigb-mobile.css section 5; hidden on desktop,
  where the rows stay a single row of 4.

  Built from what the section already renders (its View all link, its
  title, and its first three covers for the fanned stack), so
  featured-collection.liquid stays untouched. Re-runs when a section is
  re-rendered in the theme editor.
*/
(function () {
  function buildCard(ul) {
    if (!ul || ul.querySelector('.bb-view-all-slide')) return;
    var section = ul.closest('.collection');
    if (!section) return;
    var link = section.querySelector('.collection__view-all--inline');
    if (!link) return; // no "View all" = whole collection already shown

    var titleEl = section.querySelector('.collection__title .title');
    var title = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';

    var li = document.createElement('li');
    li.id = 'Slide-' + (ul.dataset.id || 'row') + '-view-all';
    li.className = 'grid__item slider__slide bb-view-all-slide';

    var a = document.createElement('a');
    a.className = 'bb-view-all-card';
    a.href = link.getAttribute('href');
    a.setAttribute('aria-label', link.getAttribute('aria-label') || 'View all');

    var stack = document.createElement('span');
    stack.className = 'bb-view-all-card__stack';
    stack.setAttribute('aria-hidden', 'true');
    var imgs = ul.querySelectorAll('.card__media img');
    for (var i = 0; i < 3 && i < imgs.length; i++) {
      var src = imgs[i].currentSrc || imgs[i].src;
      if (!src) continue;
      var im = document.createElement('img');
      im.src = src;
      im.alt = '';
      im.loading = 'lazy';
      stack.appendChild(im);
    }

    var label = document.createElement('span');
    label.className = 'bb-view-all-card__label';
    label.textContent = (link.textContent || 'View all').trim();

    a.appendChild(stack);
    a.appendChild(label);
    if (title) {
      var t = document.createElement('span');
      t.className = 'bb-view-all-card__title';
      t.textContent = title;
      a.appendChild(t);
    }
    var arrow = document.createElement('span');
    arrow.className = 'bb-view-all-card__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    a.appendChild(arrow);

    li.appendChild(a);
    ul.appendChild(li);

    // Let Refresh's slider count the new slide (arrows + "1 / n" counter)
    var slider = ul.closest('slider-component');
    if (slider && typeof slider.resetPages === 'function') {
      try { slider.resetPages(); } catch (e) {}
    }
  }

  function run(root) {
    (root || document)
      .querySelectorAll('ul.product-grid.slider--tablet')
      .forEach(buildCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(); });
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', function (e) { run(e.target); });
})();
